/**
 * THE ROW QUERY — POST /api/dashboard/reports, typed, with the two things the
 * old caller did not do.
 *
 * ── 1. IT READS THE TRUNCATION HEADERS ──
 *
 * The route sets `X-Report-Truncated` and `X-Report-Row-Limit` when a filter hit
 * the 5,000-row ceiling, and explicitly adds them to
 * `Access-Control-Expose-Headers` so a cross-origin caller can see them
 * (lobbero_be/src/CRUD/reports.js). NOTHING IN THE FRONTEND EVER READ EITHER.
 * So a truncated report rendered as if it were complete, and exported as if it
 * were complete — which is the worse half, because the file outlives the screen
 * and nobody who receives it can tell.
 *
 * ── 2. IT SENDS `limit` AND `offset` ──
 *
 * Both are accepted by the route and neither was ever sent, so the screen fetched
 * up to five thousand rows on every filter and rendered all of them. Real paging
 * is a free win and it is also what makes the ceiling reachable-but-rare instead
 * of routine.
 *
 * The BODY SHAPE IS THE SERVER'S and is not modernised: `logsFilterData` with
 * `dateVisitor` as a two-element array of `YYYY-MM-DD` and `timeVisitor` as a
 * two-element array of `HH:mm`. Every existing client and test sends exactly
 * that, and renaming it would be a wire break bought with nothing.
 */
import axios from 'axios';
import { apiRoute } from '../../../../../constants/api-constants';
import { CUSTOM_RANGE, DashboardFilter } from '../state/filter-model';
import {
  addDays,
  addMonths,
  isDayKey,
  monthStartOf,
} from '../../../../../function-utils/building-time';

/* eslint-disable @typescript-eslint/no-explicit-any */
export type RecordRow = Record<string, any>;
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * THE CLOCK A RECORDS ANSWER IS ON — from the response HEADERS, because the
 * body is still a bare array (`X-Report-*`, all exposed to the browser).
 *
 *   tz / tzSource   the zone every row's `createdAt` text is ALREADY on, and
 *                   how the server chose it (R-T12)
 *   label           "Toronto time (UTC-04:00)" — printed verbatim on an
 *                   export's cover; never derived in the browser
 *   fromDay/toDay   the window as the day keys the server resolved; ABSENT
 *                   when the request named no window
 */
export interface RecordsClock {
  tz: string | null;
  tzSource: 'viewer' | 'building' | 'fallback' | null;
  label: string | null;
  windowTz: string | null;
  windowFrom: string | null;
  windowTo: string | null;
  fromDay: string | null;
  toDay: string | null;
}

export interface RecordsResponse {
  rows: RecordRow[];
  /** True when the server hit its ceiling, so these rows are a PREFIX and every
   *  count derived from them is a floor. */
  truncated: boolean;
  rowLimit: number | null;
  /** Absent only for an answer built without the headers (a test double). */
  clock?: RecordsClock;
}

/**
 * A NAMED RANGE AS TWO DAY KEYS, counted from `today`.
 *
 * WHO STILL NEEDS THIS. Records do not: `POST /dashboard/reports` takes the
 * range NAME and resolves it on the reader's clock itself (recordsBody below).
 * The activity feed does — `GET /activity` has dates and no range parameter —
 * so the Activity tab's named range is spelled out here.
 *
 * `today` is passed in, as a day key on the reader's clock. This function used
 * to read the browser's clock itself and build keys from local getters; it now
 * does calendar arithmetic on keys and nothing else, so it cannot disagree with
 * the clock the caller chose.
 *
 * A custom range is already two keys. `this_shift` and `last_shift` are NOT
 * resolved and must never be guessed: a shift window comes from a shift row on
 * the server. For those `resolvable` is false and no dates are returned.
 */
export interface RangeCoverage {
  from?: string;
  to?: string;
  resolvable: boolean;
}

export const rangeCoverage = (
  filter: Pick<DashboardFilter, 'range' | 'from' | 'to'>,
  today: string
): RangeCoverage => {
  if (filter.range === CUSTOM_RANGE) {
    return isDayKey(filter.from) && isDayKey(filter.to)
      ? { from: filter.from, to: filter.to, resolvable: true }
      : { resolvable: false };
  }
  if (!isDayKey(today)) return { resolvable: false };

  switch (filter.range) {
    case 'today':
      return { from: today, to: today, resolvable: true };
    case 'yesterday': {
      const day = addDays(today, -1);

      return { from: day, to: day, resolvable: true };
    }
    case 'last_7_days':
      return { from: addDays(today, -7), to: today, resolvable: true };
    case 'last_30_days':
      return { from: addDays(today, -30), to: today, resolvable: true };
    case 'this_month':
      return { from: monthStartOf(today), to: today, resolvable: true };
    case 'last_month': {
      const first = addMonths(monthStartOf(today), -1);

      // The last day of the previous month = the day before the 1st of this one.
      return { from: first, to: addDays(monthStartOf(today), -1), resolvable: true };
    }
    default:
      // this_shift, last_shift, and anything the server adds later.
      return { resolvable: false };
  }
};

/**
 * The filter object -> the body this route takes.
 *
 * A field the selected book has no column for is ALREADY null by the time it
 * reaches here (filter-reducer invariant 6), so nothing has to be stripped a
 * second time — which is the whole point of putting that rule in the reducer
 * rather than in each caller.
 */
export const recordsBody = (
  filter: DashboardFilter,
  pageSize: number
): Record<string, unknown> => {
  /* THE WINDOW, AT THE TOP LEVEL OF THE BODY — and the SERVER resolves it.
   *
   * A named range goes as its NAME (`range`), a picked one as two inclusive day
   * keys (`from_day` / `to_day`). Either is turned into instants on the reader's
   * clock by the server (R-T5, R-T12), the same clock every row's time comes
   * back on. This used to compute the named range's two dates from the browser's
   * own clock and send them in `dateVisitor`.
   *
   * `dateVisitor` stays in the body, EMPTY: it is the legacy pair, the server
   * ignores it whenever one of the new fields is present, and an empty pair is
   * "no window" for anything that still reads it. */
  const custom = filter.range === CUSTOM_RANGE;
  const window: Record<string, unknown> = custom
    ? isDayKey(filter.from) && isDayKey(filter.to)
      ? { from_day: filter.from, to_day: filter.to }
      : {}
    : { range: filter.range };

  const logsFilterData: Record<string, unknown> = {
    logType: filter.book,
    dateVisitor: [undefined, undefined],
    // Read on the SAME clock as the window (the server's rule).
    timeVisitor: [filter.timeFrom || undefined, filter.timeTo || undefined],
    propertyId: filter.buildingId == null ? '' : String(filter.buildingId),
  };

  const put = (key: string, value: string | number | null) => {
    if (value == null || value === '') return;
    logsFilterData[key] = value;
  };

  put('apartmentLocation', filter.unit);
  put('visitorType', filter.visitorType);
  put('visitorDetails', filter.visitorDetails);
  put('preAuthorized', filter.preAuthorized);
  put('onPass', filter.onPass);
  put('approval', filter.approval);
  put('parkingValidation', filter.parkingValidation);
  put('numberOfGuests', filter.numberOfGuests);
  put('buildingActivity', filter.buildingActivity);
  put('priority', filter.priority);
  put('carrier', filter.carrier);
  put('status', filter.parcelStatus);
  put('name', filter.name);
  put('lastName', filter.lastName);

  return {
    logsFilterData,
    ...window,
    limit: pageSize,
    offset: filter.page * pageSize,
  };
};

export const fetchRecords = async (
  filter: DashboardFilter,
  pageSize: number,
  opts: { signal?: AbortSignal } = {}
): Promise<RecordsResponse> => {
  const response = await axios.post<RecordRow[]>(
    `${apiRoute}/dashboard/reports`,
    recordsBody(filter, pageSize),
    { signal: opts.signal }
  );

  // Header names are case-insensitive on the wire; axios lower-cases them, but
  // a proxy in between may not, so both spellings are checked.
  const headers = (response.headers || {}) as Record<string, string>;
  const truncatedRaw =
    headers['x-report-truncated'] ?? headers['X-Report-Truncated'];
  const limitRaw = headers['x-report-row-limit'] ?? headers['X-Report-Row-Limit'];

  return {
    rows: Array.isArray(response.data) ? response.data : [],
    truncated: String(truncatedRaw) === 'true',
    rowLimit: limitRaw ? Number(limitRaw) || null : null,
    clock: recordsClock(headers),
  };
};

/** The `X-Report-*` clock headers, under either spelling a proxy may leave. */
export const recordsClock = (headers: Record<string, unknown>): RecordsClock => {
  const read = (name: string): string | null => {
    const value = headers[name.toLowerCase()] ?? headers[name];

    return value == null || value === '' ? null : String(value);
  };
  const source = read('X-Report-Timezone-Source');

  return {
    tz: read('X-Report-Timezone'),
    tzSource:
      source === 'viewer' || source === 'building' || source === 'fallback' ? source : null,
    label: read('X-Report-Timezone-Label'),
    windowTz: read('X-Report-Window-Timezone'),
    windowFrom: read('X-Report-Window-From'),
    windowTo: read('X-Report-Window-To'),
    fromDay: read('X-Report-From-Day'),
    toDay: read('X-Report-To-Day'),
  };
};

/** GET /api/logs — one building, one desk day, both books. The Reports tab's
 *  single-day view, which is the whole of the old /dashboard/view-edit-export. */
export interface DayLogsResponse {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  visitorLogs: any[];
  dailyLogs: any[];
  /* eslint-enable @typescript-eslint/no-explicit-any */
  /** The clock to SHOW the day on, by who asked (R-T12)… */
  tz?: string;
  tz_source?: 'viewer' | 'building' | 'fallback';
  /** …and the desk day itself, which is always the BUILDING's: `window.tz` is
   *  the building's own zone. Null when no `date` was asked. */
  window?: { from: string; to: string; tz: string; day: string } | null;
}

export const fetchDayLogs = async (
  buildingId: number,
  /** A day KEY — the first day of the picked range, as it was picked. */
  date: string,
  opts: { signal?: AbortSignal } = {}
): Promise<DayLogsResponse> => {
  // The desk day is 07:00 to 07:00 on the BUILDING's clock, for every caller;
  // the server cuts it. Nothing about the reader's zone is sent here — it goes
  // out once, as the X-Client-Timezone header (axios-setup.ts).
  const { data } = await axios.get<DayLogsResponse>(`${apiRoute}/logs`, {
    params: { buildingId, date },
    signal: opts.signal,
  });

  return {
    visitorLogs: data?.visitorLogs || [],
    dailyLogs: data?.dailyLogs || [],
    tz: data?.tz,
    tz_source: data?.tz_source,
    window: data?.window ?? null,
  };
};
